import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Copy, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#c9a962';
const INK = '#14100e';

export const FoundingTenBanner = ({ userId }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/api/founding-ten/status/${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setStatus(d))
      .catch(() => {});
  }, [userId]);

  if (!status) return null;

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/invite/${status.referral_code}`);
    toast.success('Your Founding Ten invite link is copied — send it to 5 friends!');
  };

  if (status.completed) {
    return (
      <div className="mx-4 mt-4 rounded-xl p-5 text-center" style={{ backgroundColor: INK, border: `1px solid ${GOLD}` }} data-testid="founding-ten-winner-card">
        <Crown className="w-8 h-8 mx-auto mb-2" style={{ color: GOLD }} />
        <p className="text-lg" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>Founding Ten — Member #{status.rank}</p>
        <p className="text-xs mt-1" style={{ color: '#a89a8c' }}>Lifetime 0% fees · free boosts · your name on the Founders Wall</p>
        <button onClick={() => navigate('/founders-wall')} className="mt-3 text-xs underline" style={{ color: GOLD }} data-testid="founding-ten-wall-link">
          View the Founders Wall
        </button>
      </div>
    );
  }

  if (!status.active) return null;

  const itemsPct = Math.min(100, (status.items_listed / status.target_items) * 100);
  const refsPct = Math.min(100, (status.referrals_qualified / status.target_referrals) * 100);

  return (
    <div className="mx-4 mt-4 rounded-xl p-5" style={{ backgroundColor: INK, border: `1px solid rgba(201,169,98,0.5)` }} data-testid="founding-ten-banner">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4" style={{ color: GOLD }} />
          <p className="text-[11px] tracking-[0.25em] uppercase" style={{ color: GOLD }}>The Founding Ten</p>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(201,169,98,0.15)', color: GOLD }} data-testid="founding-ten-slots">
          {status.slots_left} of 10 slots left
        </span>
      </div>
      <p className="text-sm mb-4" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>
        Complete the challenge. Join Minted history. Lifetime 0% fees & free boosts.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
          <p className="text-3xl font-bold" style={{ color: '#f0e9df' }} data-testid="founding-ten-items-countdown">{status.items_remaining}</p>
          <p className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: '#a89a8c' }}>listings to go</p>
          <div className="h-1 rounded-full mt-2" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
            <div className="h-1 rounded-full transition-all" style={{ width: `${itemsPct}%`, backgroundColor: GOLD }} />
          </div>
          <p className="text-[10px] mt-1" style={{ color: '#a89a8c' }}>{status.items_listed} / {status.target_items} listed</p>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
          <p className="text-3xl font-bold" style={{ color: '#f0e9df' }} data-testid="founding-ten-refs-countdown">{status.referrals_remaining}</p>
          <p className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: '#a89a8c' }}>referrals to go</p>
          <div className="h-1 rounded-full mt-2" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
            <div className="h-1 rounded-full transition-all" style={{ width: `${refsPct}%`, backgroundColor: GOLD }} />
          </div>
          <p className="text-[10px] mt-1" style={{ color: '#a89a8c' }}>{status.referrals_qualified} / {status.target_referrals} friends listed</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => navigate('/sell')} className="flex-1 text-xs font-semibold py-2.5 rounded-lg" style={{ backgroundColor: GOLD, color: INK }} data-testid="founding-ten-list-btn">
          List an item
        </button>
        <button onClick={copyLink} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2.5 rounded-lg" style={{ border: `1px solid ${GOLD}`, color: GOLD }} data-testid="founding-ten-copy-btn">
          <Copy className="w-3 h-3" /> Copy invite link
        </button>
      </div>
      <p className="flex items-center justify-center gap-1 text-[10px] mt-3" style={{ color: '#a89a8c' }}>
        <Clock className="w-3 h-3" /> {status.days_left} days remaining — referrals count once your friend lists an item
      </p>
    </div>
  );
};
